import React, { useState, useReducer, useEffect, useRef, useContext, forwardRef } from "react";
import MortgageCalculator from "./components/Calculator/MortgageCalculator";
import MortgageDetailModal from "./components/MortgageDetail/MortgageDetailModal";
import { CONSTANTS, formElements, emailFormValues, propertyUsageTypes, MortgageCategory, ConstructionLoanType, LoanType } from "./common/constants";
import { FeeType } from "./common/FeeType";
import { ELOQUA } from "../../utilities/enums";
import { REDUCERS } from "../../reducers/reducerConstants";
import { DropdownValues, DROPDOWN_IDS, VeteranType, PropertyUsageType } from "./common/DropdownValues";
import { getLoanToValue } from "./common/helpers";
import { getRates, getFirstTimeLoanAmount, getDetailedQuote, sendEmailedQuote, getMrqtUsStates, getCountiesByStateId, getLoanLimitByStateCounty, getDefaultLoanLimit, getLoanMaxLTVs } from "./services/MortgageService";
import HeaderHeightContext from "../../context/HeaderContext";
import ModalContext from "../../context/ModalContext";
import { valuesReducer } from "../../reducers/valuesReducer";
import { dropdownReducer } from "../../reducers/dropdownReducer";
import IEditableProps from "../../utilities/IEditableProps";
import { tryParse } from "./common/helpers";
import IStyledProps from "../../utilities/IStyledProps";
import classNames from "classnames";
import useLanguage from "../../utilities/useLanguage";
import { getTranslation } from "./translations";
import { getMortgageCategory } from "./common/MortageCategories";
import { getAutomatedUnderwritingSystem } from "./common/AutomatedUnderwritingSystem";
import { calculateFees } from "./common/feeHelpers";
import { ICalculatedRate } from "./models/IRate";
import { IMortageQuote } from "./models/IMortageQuote";
import { IRate } from "./models/IRate";
import { ILtvMap, IEmployee, IFormValues, IEmailForm, IRateConfig } from "./models";
import { LoanRate } from "./common/LoanRate";
import { getUniqueModalId } from "../Modals/BaseModal";
import getAmortizationTypes from "./common/AmortizationTypes";

export interface IMrqtProps extends IStyledProps, IEditableProps {
    headline: string,
    purchasePrice: string,
    downPayment: string,
    currentHomeValue: string,
    refinanceAmount: string,
    employeeString: string,
    ratesString: string,
    otherDetailsHeader: string,
    showEmailForm: boolean,
    disclaimer: string,
    otherDetailsLabel: string,
    referrerId: string,
    loanPurpose: string,
    imageLeftSrc: string,
    imageRightSrc: string,
    imageLeftAlt: string,
    imageRightAlt: string,
    footerHtml: string
}

const MRQT = forwardRef((props: IMrqtProps, ref: any) => {
    const [ uniqueModalId ] = useState(getUniqueModalId());
    // default loanPurpose to 'Purchase' for MRQT usages that don't have loanPurpose prop
    const { headline, purchasePrice, ratesString, employeeString, downPayment, otherDetailsHeader, showEmailForm, disclaimer, otherDetailsLabel, cmsId, editorClassObj, referrerId, loanPurpose = 'Purchase', currentHomeValue, refinanceAmount, imageLeftSrc, imageRightSrc, imageLeftAlt, imageRightAlt, footerHtml } = props;
    const { lang } = useLanguage();
    let employee: IEmployee = tryParse<IEmployee>(employeeString);

    if (!employee) {
        employee = { applyNowUrl: "https://secure.macu.com/#/milestones?loanType=MORTGAGE" };
    }

    const rateConfigs: IRateConfig[] = tryParse<IRateConfig[]>(ratesString);
    const [values, valuesDispatch] : [IFormValues, any] = useReducer(valuesReducer, {
        ...formElements,
        LoanPurpose: { value: loanPurpose, isValid: true, required: true },
        PurchasePrice: { value: getAmount(purchasePrice, formElements.PurchasePrice.value), isValid: true, required: true },
        DownPayment: { value: getAmount(downPayment, formElements.DownPayment.value), isValid: true, required: true },
        CurrentHomeValue: { value: getAmount(currentHomeValue, formElements.CurrentHomeValue.value), isValid: true, required: true },
        RefinanceAmount: { value: getAmount(refinanceAmount, formElements.RefinanceAmount.value), isValid: true, required: true },
        VeteranType: { value: VeteranType[0].id, isValid: true, required: true },
    });
    const headerHeight = useContext(HeaderHeightContext);
    const [emailValues, emailValuesDispatch] = useReducer(valuesReducer, emailFormValues);
    const [labels, setLabels] = useState([loanPurpose !== CONSTANTS.PURCHASE ? CONSTANTS.CURRENT_VALUE : CONSTANTS.PURCHASE_PRICE, loanPurpose !== CONSTANTS.PURCHASE ? CONSTANTS.REFINANCE_AMT : CONSTANTS.DOWN_PAYMENT]);
    const [dirty, setDirty] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [aprRates, setAprRates] = useState<{loaded: boolean, data: IRate[]}>({ loaded: false, data: [] });
    const [maxLTVRates, setMaxLTVRates] = useState<ILtvMap>();
    const [loan, setLoan] = useState<{loanAmount: number, rate: ICalculatedRate}>({ loanAmount: 0, rate: null});
    const [details, setDetails] = useState<IMortageQuote>();
    const [spinner, setSpinner] = useState(false);
    const [show, setShow] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const countiesLoaded = useRef(false);
    const [dropdownState, dropdownDispatch] = useReducer(dropdownReducer, {
        [DROPDOWN_IDS.LOAN_PURPOSE]: { id: DROPDOWN_IDS.LOAN_PURPOSE, name: "Loan Purpose" },
        [DROPDOWN_IDS.LOAN_TERM]: { id: DROPDOWN_IDS.LOAN_TERM, name: "Loan Term" },
        [DROPDOWN_IDS.USAGE_TYPE]: { id: DROPDOWN_IDS.USAGE_TYPE, name: "Usage Type" },
        [DROPDOWN_IDS.PROPERTY_TYPE]: { id: DROPDOWN_IDS.PROPERTY_TYPE, name: "Property Type" },
        [DROPDOWN_IDS.STATE]: { id: DROPDOWN_IDS.STATE, name: "State" },
        [DROPDOWN_IDS.COUNTY]: { id: DROPDOWN_IDS.COUNTY, name: "County" },
        [DROPDOWN_IDS.CREDIT_SCORE]: { id: DROPDOWN_IDS.CREDIT_SCORE, name: "Credit Score" },
        [DROPDOWN_IDS.VETERAN_TYPE]: { id: DROPDOWN_IDS.VETERAN_TYPE, name: "Veteran Type" }
    });
    const rateId = Number(values.LoanTerm.value);
    const loanType = getLoanType(loan.rate);
    const mortgageCategory = getMortgageCategory(rateId);
    const { openModal } = useContext(ModalContext);
    const isFthbLoan = [MortgageCategory.FTHB, MortgageCategory.FTHB_ARM].includes(mortgageCategory);

    useEffect(() => {
        function loadDropdownItems() {
            DropdownValues.forEach(item => {
                const itemData = item.data.map(dropdowm => ({
                    ...dropdowm,
                    name: getTranslation(lang)[dropdowm.id] || dropdowm.name,
                }));
                dropdownDispatch({ type: REDUCERS.SET_DROPDOWN_ITEMS, id: item.id, value: itemData });
            });
        }

        async function loadMaxLTVs() {
            const ltvData = await getLoanMaxLTVs();
            setMaxLTVRates(ltvData);
        }

        async function loadRates() {
            //grab the RateID
            const rateIds = rateConfigs.map(rate => rate.id);
            //get the rates
            const ratesFromApi = await getRates(rateIds);
            //merge the objects
            const concatRates = ratesFromApi.map(apiRate => {
                const rateConfig: IRateConfig = rateConfigs.find(rateConfig => Number(rateConfig.id) === apiRate.id);
                if (rateConfig) {
                    return { ...apiRate, rateConfig };
                } else {
                    return null;
                }
            });

            setAprRates({ loaded: true, data: concatRates });

            dropdownDispatch({
                type: REDUCERS.SET_DROPDOWN_ITEMS,
                id: DROPDOWN_IDS.LOAN_TERM,
                value: rateConfigs.map((rate: IRateConfig) => (
                    {
                        id: rate.id,
                        name: rate.displayName,
                    }
                ))
            });
        }

        async function loadStates() {
            const result = await getMrqtUsStates();
            dropdownDispatch({ type: REDUCERS.SET_DROPDOWN_ITEMS, id: DROPDOWN_IDS.STATE, value: result });
        }

        async function loadDefaultLoanLimit() {
            const result = await getDefaultLoanLimit();
            valuesDispatch({ type: REDUCERS.SET_VALUE, name: "DefaultLoanLimit", value: result });
        }

        async function loadFirstAmount() {
            const firstTimeAmountLimit = await getFirstTimeLoanAmount();
            valuesDispatch({ type: REDUCERS.SET_VALUE, name: "FirstTimeLimit", value: firstTimeAmountLimit });
        }

        loadDropdownItems();
        loadRates();
        loadStates();
        loadFirstAmount();
        loadDefaultLoanLimit();
        loadMaxLTVs();
    }, []);

    // Load counties when state is changed
    useEffect(() => {
        if (countiesLoaded.current) {
            loadCounties();
        } else {
            countiesLoaded.current = true;
            loadCounties(2795); // default of 2795 is 'Salt Lake' county
        }
    }, [values.State.value]);

    useEffect(() => {
        const countyId = values.County.value;
        const stateId = values.State.value;
        const defaultLoanLimit = values.DefaultLoanLimit.value;
        const stateListLoaded = dropdownState[DROPDOWN_IDS.STATE].data?.length > 0;
        const countyListLoaded = dropdownState[DROPDOWN_IDS.COUNTY].data?.length > 0;

        if (stateId && countyId && defaultLoanLimit && stateListLoaded && countyListLoaded) {
            loadLoanLimit(stateId, countyId, defaultLoanLimit);
        }
    }, [values.State.value, values.County.value, values.DefaultLoanLimit.value, dropdownState[DROPDOWN_IDS.STATE].data, dropdownState[DROPDOWN_IDS.COUNTY].data]);

    // Change labels when LoanPurpose dropdown is changed
    useEffect(() => {
        const isRefinance = (values.LoanPurpose.value !== CONSTANTS.PURCHASE && values.LoanPurpose.value !== CONSTANTS.LOAN_PURPOSE);

        if (isRefinance) {
            setLabels([getTranslation(lang).currentHomeValue, getTranslation(lang).refinanceAmount]);
        } else {
            setLabels([getTranslation(lang).purchasePrice, getTranslation(lang).downPayment]);
        }
    }, [values.LoanPurpose.value]);

    // These useEffects cause the widget preview to update visually when changing the widget props via buildr
    // loanPurpose, purchasePrice, downPayment, currentHomeValue, refinanceAmount
    useEffect(() => {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: CONSTANTS.LOAN_PURPOSE_ID, value: loanPurpose });
    }, [loanPurpose]);
    useEffect(() => {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: CONSTANTS.PURCHASE_PRICE_ID, value: purchasePrice });
    }, [purchasePrice]);
    useEffect(() => {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: CONSTANTS.DOWN_PAYMENT_ID, value: downPayment });
    }, [downPayment]);
    useEffect(() => {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: CONSTANTS.CURRENT_VALUE_ID, value: currentHomeValue });
    }, [currentHomeValue]);
    useEffect(() => {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: CONSTANTS.REFINANCE_AMT_ID, value: refinanceAmount });
    }, [refinanceAmount]);


    // For Lot loans disable all property usage types other than INVESTMENT_PROPERTY
    useEffect(() => {
        const isLotLoan = mortgageCategory === MortgageCategory.LOT;
        const { PRIMARY_RESIDENCE, INVESTMENT_PROPERTY } = propertyUsageTypes;
        if (isLotLoan || isFthbLoan) {
            valuesDispatch({
                type: REDUCERS.SET_VALUE,
                name: DROPDOWN_IDS.USAGE_TYPE,
                value: isLotLoan ? INVESTMENT_PROPERTY : PRIMARY_RESIDENCE
            });
        }

        dropdownDispatch({
            type: REDUCERS.SET_DROPDOWN_ITEMS,
            id: DROPDOWN_IDS.USAGE_TYPE,
            value: PropertyUsageType.map((type) => ({
                ...type,
                name: getTranslation(lang)[type.id],
                disabled:
                       (isLotLoan && type.id !== INVESTMENT_PROPERTY)
                    || (isFthbLoan && type.id !== PRIMARY_RESIDENCE)
            }))
        });
    }, [mortgageCategory]);

    function getAmount(value: string|undefined|null, fallback: string) {
        let returnValue = (value === '' || value === null || value === undefined) ? fallback : value;
        return typeof returnValue === 'string' ? returnValue?.replace(/-/g, '') : returnValue;
    }

    async function loadCounties(defaultValue?: number) {
        setFormLoading(true);
        const counties = await getCounties();
        dropdownDispatch({
            type: REDUCERS.SET_DROPDOWN_ITEMS,
            id: CONSTANTS.COUNTY,
            value: counties
        });
        if (defaultValue) {
            valuesDispatch({ type: REDUCERS.SET_VALUE, name: "County", value: defaultValue });
        } else if (counties[0]) {
            valuesDispatch({ type: REDUCERS.SET_VALUE, name: "County", value: counties[0].id });
        }
        setFormLoading(false);
    }

    async function getCounties() {
        const countiesArray = await getCountiesByStateId(values.State.value);
        return countiesArray?.length ? countiesArray : [];
    }

    async function loadLoanLimit(stateId: string, countyId: string, defaultLoan: number) {
        const state: string = dropdownState[DROPDOWN_IDS.STATE].data.find(state => state.id == stateId)?.abbreviation;
        const county: string = dropdownState[DROPDOWN_IDS.COUNTY].data.find(county => county.id == countyId)?.name;
        const result = await getLoanLimitByStateCounty(state, county, defaultLoan);
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: "LoanLimit", value: result });
    }

    function getLoanType(rate: IRate) {
        return rate?.rate?.mortgageLoanType;
    }

    function getConstructionType(mortgageCategory: MortgageCategory): ConstructionLoanType|undefined {
        const isContruction = mortgageCategory === MortgageCategory.CONSTRUCTION;
        if (isContruction) {
            return ConstructionLoanType.ConstructionToPermanent;
        }
        return undefined;
    }

    function mutateRateValues(loanTerm: number, loanType: LoanType) {
        let State = '';
        const stateMatch = dropdownState[DROPDOWN_IDS.STATE].data.find(state => state.id == values.State.value);
        if (stateMatch) {
            State = stateMatch.abbreviation || '';
        }

        let County = '';
        const countyMatch = dropdownState[DROPDOWN_IDS.COUNTY].data.find(county => county.id == values.County.value);
        if (countyMatch) {
            County = countyMatch.name || '';
        }

        const loanAmount = values.LoanPurpose.value === CONSTANTS.PURCHASE ? values.LoanAmount.value : values.RefinanceAmount.value;
        const purchasePrice = isFthbLoan ? values.PurchasePrice.value - values.DownPayment.value : values.PurchasePrice.value;
        const propertyAmount = values.LoanPurpose.value === CONSTANTS.PURCHASE ? purchasePrice : values.CurrentHomeValue.value;
        const isBalloon = loan?.rate?.rate?.disclosureInformation?.isBalloonLoan;
        // Only retrieve all products for loan ID_FIRST_BUYER_RATE, not for all FTHB loans
        const retrieveSingleProduct = LoanRate.FIRST_TIME_HOMEBUYER !== Number(values.LoanTerm.value);

        return {
            AmortizationTypes: getAmortizationTypes(rateId, mortgageCategory, isBalloon),
            CreditScore: parseInt(values.CreditScore.value),
            County,
            FinancedUnitCount: 1,
            LoanAmount: parseInt(loanAmount),
            LoanPurpose: values.LoanPurpose.value,
            PropertyAmount: parseInt(propertyAmount),
            PropertyType: values.PropertyType.value,
            PropertyUsageType: values.PropertyUsageType.value,
            State,
            LoanTerm: loanTerm,
            LoanType: loanType,
            FirstTimeHomeBuyer: isFthbLoan ? true : undefined,
            TypeOfVeteran: loanType === LoanType.VA ? values.VeteranType.value : undefined,
            VeteranFirstTimeUse: loanType === LoanType.VA ? true : undefined,
            ConstructionLoanType: getConstructionType(mortgageCategory),
            AutomatedUnderwritingSystem: getAutomatedUnderwritingSystem(mortgageCategory),
            IsOnlyProductWithBestRateReturned: retrieveSingleProduct,
        };
    }

    async function submitForm() {
        try {
            const productCode = loan?.rate.rate?.product?.symitarName;
            const loanType = getLoanType(loan.rate);
            values.LoanAmount.value = parseFloat(values.PurchasePrice.value) - parseFloat(values.DownPayment.value);
            const mutatedValues = mutateRateValues(loan.rate.loanTerm, loanType);
            setSpinner(true);
            const results = await getDetailedQuote(mutatedValues);
            if (results.length) {
                const mortgageData = results.find(mortgageItem => mortgageItem.ProductCode === productCode);
                setDetailedData(mortgageData ? mortgageData : results[0]);
            } else {
                throw new Error("No detailed quote available");
            }

        } catch (error) {
            console.error(error);
            setDetails(null);
            setSpinner(false);
            setShow(true);
            setEmailSent(false);
        }
    }

    function setDetailedData(mortgageData: IMortageQuote) {
        setDetails({
            ...mortgageData,
            FeeMap: calculateFees(mortgageData.Price, mortgageData.FeeMap)
        });
        setShow(true);
        setSpinner(false);
        setDirty(true);
        setEmailSent(false);
    }

    function onRateChange(loanAmount: number, rate: ICalculatedRate) {
        setLoan({loanAmount, rate});
    }

    //close the modal
    function onCloseModal() {
        // Clear these values on modal close to prevent them from being included if reopened.
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: "Insurance", value: 0 });
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: "PropertyTax", value: 0 });
    }

    //show the table
    function onDetailsClick() {
        if (openModal) {
            openModal(uniqueModalId);
        }
        setEmailSent(false);
    }

    function onChange(name: string, value: string) {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name, value });
        if (dirty) {
            setShow(false);
        }
    }

    function onEmailFormChange(name: string, value: string, isValid: boolean) {
        emailValuesDispatch({ type: REDUCERS.SET_VALUE, name, value, isValid });
    }

    function getEmailDetails(email, details: IMortageQuote, employeeId = ""): FormData {
        const date = new Date();
        const ltv = getLoanToValue(loan.loanAmount, values.PurchasePrice.value);

        const emailDetails = new FormData();

        emailDetails.append("FirstName", email.FirstName.value);
        emailDetails.append("LastName", email.LastName.value);
        emailDetails.append("EmailAddress", email.Email.value);
        emailDetails.append("LoanPurpose", values.LoanPurpose.value);
        emailDetails.append("State", values.State.value);
        emailDetails.append("County", values.County.value);
        emailDetails.append("CreditScore", values.CreditScore.value);
        emailDetails.append("PropertyType", values.PropertyType.value);
        emailDetails.append("PropertyUsageType", values.PropertyUsageType.value);
        emailDetails.append("LoanAmount", loan.loanAmount.toString());
        emailDetails.append("PropertyValue", values.PurchasePrice.value);
        emailDetails.append("RateDate", date.toLocaleDateString());
        emailDetails.append("Payment", loan.rate.monthlyPayment.toString());
        emailDetails.append("Rate", loan.rate.rate.initialRate.toString());
        emailDetails.append("Points", loan.rate.rate.discountPoints.toString());
        emailDetails.append("APR", loan.rate.rate.disclosureInformation.apr.toString());
        emailDetails.append("LTV", ltv.substring(0, ltv.length - 1));
        emailDetails.append("LoanProgram", loan.rate.name);
        emailDetails.append("LockPeriod", details.LockPeriod.toString());
        emailDetails.append("OriginationFee", details.FeeMap[FeeType.LOAN_ORIGINATION].toString());
        emailDetails.append("ProcessingFee", details.FeeMap[FeeType.PROCESSING].toString());
        emailDetails.append("MortgagePoints", details.FeeMap[FeeType.POINTS].toString());
        emailDetails.append("TotalLenderFees", details.FeeMap[FeeType.TOTAL_LENDER_FEES].toString());
        emailDetails.append("AppraisalFee", details.FeeMap[FeeType.APPRAISAL].toString());
        emailDetails.append("CreditReportFee", details.FeeMap[FeeType.CREDIT_REPORT].toString());
        emailDetails.append("TitleFees", details.FeeMap[FeeType.TITLE_SERVICES].toString());
        emailDetails.append("TotalEstimatedThirdPartyCosts", details.FeeMap[FeeType.TOTAL_THIRD_PARTY].toString());
        emailDetails.append("TotalEstimatedClosingCosts", details.TotalClosingCost.toString());
        emailDetails.append("EmployeeId", employeeId);
        emailDetails.append("elqFormName", ELOQUA.MORTGAGE_FORM);
        emailDetails.append("elqSiteID", ELOQUA.SITE_ID);

        return emailDetails;
    }

    async function sendEmail(email: IEmailForm) {
        const emailDetails = getEmailDetails(email, details, employee.id);
        const sent = await sendEmailedQuote(emailDetails);
        if (sent.toString().match("https://s2213.t.eloqua.com/e/DefaultFormSubmitConfirmation.aspx")) {
            setEmailSent(true);
        }
    }

    const taxes = values.PropertyTax.value;
    const insurance = values.Insurance.value;
    const loanName = loan?.rate?.rateConfig.displayName ?? '';
    const loanLabel = loan?.rate?.rateConfig.label ?? '';

    const detailProps = {
        modalId: uniqueModalId,
        loanName,
        loanLabel,
        loanType,
        onCloseModal: onCloseModal,
        showEmailForm,
        onChange,
        onEmailFormChange,
        values,
        emailValues,
        spinner,
        formLoading,
        emailSent,
        employee,
        labels,
        dropdownState,
        headerHeight,
        taxes,
        insurance,
        dirty,
        submitForm,
        sendEmail,
        details,
        show,
        otherDetailsLabel,
        otherDetailsHeader,
        disclaimer,
        referrerId,
        maxLTVRates,
    };

    const calcProps = {
        cmsId,
        className: props.className,
        onDetailsClick,
        onRateChange,
        onChange,
        values,
        dropdownState,
        aprRates,
        labels,
        headline,
        maxLTVRates,
        imageLeftSrc,
        imageRightSrc,
        imageLeftAlt,
        imageRightAlt,
        footerHtml,
    };
    return (
        <div data-id={cmsId} ref={ref} className={classNames('overflow-hidden', editorClassObj)}>
            <MortgageCalculator {...calcProps} />
            <MortgageDetailModal {...detailProps} />
        </div>
    );
});

export default MRQT;